import {
  PROJECT_FILES_BUCKET,
  type ProjectFile,
  projectFileSchema,
  SCRIPT_CONTENT_TYPE,
  scriptObjectName,
} from '@noconflict/contracts';
import type { Supabase } from '../../shared/api/supabase';

/**
 * The project's script (RLS: whoever may read the project), or null when none has been uploaded. An ordered
 * project may hold later versions too; this is the original, the oldest (a draft holds only one).
 */
export async function getScriptFile(
  client: Supabase,
  projectId: string,
): Promise<ProjectFile | null> {
  const { data, error } = await client
    .from('project_files')
    .select('*')
    .eq('project_id', projectId)
    .eq('kind', 'script')
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? projectFileSchema.parse(data) : null;
}

/**
 * Puts a script PDF at `<project>/scripts/<file id>.pdf`. Storage accepts it only on a script project the caller
 * may edit that is a draft or ordered (a version), and never over an existing object; the file's own name is not
 * part of the path.
 */
export async function uploadScriptObject(
  client: Supabase,
  projectId: string,
  fileId: string,
  file: File,
): Promise<void> {
  const { error } = await client.storage
    .from(PROJECT_FILES_BUCKET)
    .upload(scriptObjectName(projectId, fileId), file, {
      upsert: false,
      contentType: SCRIPT_CONTENT_TYPE,
    });
  if (error) throw new Error(error.message);
}
